import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getCached, formatPrice, formatTime, formatDateShort, formatDate } from "../../lib/api";
import { Reveal, FloorLabel, CountUp, useInView } from "../motion/Reveal";
import { usePlot } from "../../lib/plot";
import { LOCALE, t } from "../../lib/i18n";

const W = 640, H = 220, PAD = 4;
const RANGES = [["1mo", t("1 mnd."), t("1 måned")], ["6mo", t("6 mnd."), t("6 måneder")], ["1y", t("12 mnd."), t("12 måneder")]];

const scaleOf = (vals) => { const min = Math.min(...vals), max = Math.max(...vals); return { min, span: max - min || 1 }; };
const yOf = (v, s) => PAD + (1 - (v - s.min) / s.span) * (H - PAD * 2);

/** Interactive area chart. Pure SVG, no libs. Plots itself in once visible, morphs between ranges, crosshair + price on hover. */
const AreaChart = ({ points, summary, range, active }) => {
  const uid = useId().replace(/:/g, "");
  const [ref, seen] = useInView({ threshold: 0.25 });
  const [hover, setHover] = useState(null);
  const dotRef = useRef(null);

  const has = points?.length > 1;
  const geo = useMemo(() => (has ? scaleOf(points.map((p) => p.close)) : null), [points, has]);
  const ys = useMemo(() => (has ? points.map((p) => yOf(p.close, geo)) : null), [points, has, geo]);
  const placeDot = useCallback((x, y) => { const el = dotRef.current; if (el) { el.style.left = `${(x / W) * 100}%`; el.style.top = `${(y / H) * 100}%`; } }, []);
  const { lineRef, areaRef, wipeRef, phase } = usePlot({ ys, xw: W, yBase: H, seriesKey: range, ready: active && seen, placeDot });

  if (!has) return <div data-testid="oslo-bors-sparkline" className="h-40 md:h-56 flex items-end t-small text-muted">{t("Historikk utilgjengelig")}</div>;
  const first = points[0]?.date, last = points[points.length - 1]?.date;
  const drawn = phase === "drawn";

  const onMove = (e) => {
    if (!drawn) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    setHover(Math.round(x * (points.length - 1)));
  };
  const hp = hover != null ? points[hover] : null;
  const hx = hp ? (hover / Math.max(1, points.length - 1)) * W : 0;
  const hy = hp ? yOf(hp.close, geo) : 0;

  return (
    <div ref={ref} data-testid="oslo-bors-sparkline" data-range={range} data-phase={phase}>
      <div className="relative select-none" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-40 md:h-56 w-full overflow-visible" role="img" aria-label={t("Kursutvikling. Høyeste {high}, laveste {low} kroner.", { high: summary ? formatPrice(summary.high) : "", low: summary ? formatPrice(summary.low) : "" })}>
          <defs>
            <linearGradient id={`area-${uid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--eqva-accent)" stopOpacity="0.22" />
              <stop offset="100%" stopColor="var(--eqva-accent)" stopOpacity="0" />
            </linearGradient>
            <clipPath id={`wipe-${uid}`}><rect ref={wipeRef} x="0" y="-12" width="0" height={H + 24} /></clipPath>
          </defs>
          <g clipPath={`url(#wipe-${uid})`}>
            <line x1="0" x2={W} y1={H} y2={H} stroke="var(--eqva-line)" vectorEffect="non-scaling-stroke" />
            <path ref={areaRef} fill={`url(#area-${uid})`} />
            <path ref={lineRef} fill="none" stroke="var(--eqva-accent)" strokeWidth="1.75" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
          </g>
          {hp && <line x1={hx} x2={hx} y1={0} y2={H} stroke="var(--eqva-navy)" strokeOpacity="0.4" vectorEffect="non-scaling-stroke" />}
        </svg>
        <span ref={dotRef} className={`chart-dot absolute h-2.5 w-2.5 -ml-[5px] -mt-[5px] rounded-full bg-accent ring-4 ring-surface ${phase !== "idle" ? "is-on" : ""} ${drawn ? "is-landed" : ""} ${hp ? "is-hidden" : ""}`} aria-hidden="true" />
        {hp && (
          <>
            <span className="absolute h-3 w-3 -ml-1.5 -mt-1.5 rounded-full bg-navy ring-4 ring-surface" style={{ left: `${(hx / W) * 100}%`, top: `${(hy / H) * 100}%` }} aria-hidden="true" />
            <span data-testid="share-chart-tooltip" className={`fade-in absolute top-0 tabular bg-navy text-white text-[13px] leading-tight px-3 py-2 whitespace-nowrap ${hx > W * 0.7 ? "-translate-x-full -ml-3" : "ml-3"}`} style={{ left: `${(hx / W) * 100}%`, animationDuration: "160ms" }}>
              <span className="block text-[16px] font-medium">{formatPrice(hp.close)} NOK</span>
              <span className="block mt-0.5 text-white/70">{formatDate(hp.date)}</span>
            </span>
          </>
        )}
      </div>
      <div data-chart={drawn ? "in" : "out"} className="mt-3 flex items-center justify-between tabular t-small text-muted">
        <span className="cq" style={{ "--d": "0ms" }} key={`f-${range}`}>{first ? formatDateShort(first) : ""} {first ? new Date(first).getFullYear() : ""}</span>
        {summary && <span className="cq" style={{ "--d": "90ms" }} key={`s-${range}`}>{t("Høy")} {formatPrice(summary.high)} · {t("Lav")} {formatPrice(summary.low)}</span>}
        <span className="cq" style={{ "--d": "180ms" }} key={`l-${range}`}>{last ? formatDateShort(last) : ""} {last ? new Date(last).getFullYear() : ""}</span>
      </div>
    </div>
  );
};

const FieldHead = ({ label, sub, className = "", style }) => (
  <div className={`flex items-baseline justify-between gap-6 ${className}`} style={style}>
    <p className="t-label text-muted">{label}</p>
    {sub ? <p className="t-small text-muted text-right">{sub}</p> : null}
  </div>
);

export const ShareCard = ({ active: activeProp }) => {
  const [ref, seen] = useInView({ threshold: 0.2 });
  const [q, setQ] = useState(null);
  const [range, setRange] = useState("1y");
  const [hist, setHist] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(false);
  useEffect(() => { getCached("/share").then(setQ).catch(() => setErr(true)); }, []);
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    getCached(`/share/history?range=${range}`, 5 * 60_000)
      .then((d) => { if (!cancelled) { setHist(d); setBusy(false); } })
      .catch(() => { if (!cancelled) { setHist((h) => h || { points: [], summary: null }); setBusy(false); } });
    return () => { cancelled = true; };
  }, [range]);

  const active = (activeProp ?? seen) && !!q;
  const up = q ? q.change_pct >= 0 : true;
  const label = q ? (q.fallback ? t("Reserveverdi · ikke sanntid") : q.market_state === "open" ? t("Forsinket kurs · sist oppdatert {time}", { time: formatTime(q.timestamp) }) : t("Sist omsatt {time}", { time: formatTime(q.timestamp) })) : "";
  const chg = hist?.summary?.change_pct;
  const shownRange = hist?.range || range;
  const rangeLabel = RANGES.find((r) => r[0] === shownRange)?.[2];

  return (
    <div ref={ref} data-testid="oslo-bors-share" data-seq={active ? "in" : "out"} className="flex h-full flex-col">
      {err ? (
        <p data-testid="oslo-bors-share-error" className="t-small text-muted">{t("Kursinformasjon er ikke tilgjengelig akkurat nå.")}</p>
      ) : !q ? (
        <div className="h-72 animate-pulse bg-[rgba(8,28,48,0.05)]" aria-busy="true" />
      ) : (
        <>
          <FieldHead label={t("Aksjen")} className="sq" style={{ "--d": "0ms" }} />
          <div className="mt-8 flex items-baseline gap-5 flex-wrap">
            <span data-testid="oslo-bors-share-price" className="sq tabular text-[clamp(3.5rem,6.5vw,7rem)] font-medium leading-[0.85] tracking-[-0.045em] text-navy" style={{ "--d": "60ms" }}>
              <CountUp value={q.price} decimals={2} duration={1500} delay={120} active={active} /><span className="ml-3 text-[0.22em] font-medium tracking-normal text-muted">NOK</span>
            </span>
            <span data-testid="oslo-bors-share-change" className={`sq tabular text-base font-medium ${up ? "text-up" : "text-down"}`} style={{ "--d": "520ms" }}>
              {up ? "▲" : "▼"} {Math.abs(q.change_pct).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} % {t("i dag")}
            </span>
          </div>
          <div className="mt-12 flex items-baseline justify-between gap-6 flex-wrap">
            <div role="tablist" aria-label={t("Periode")} aria-busy={busy || undefined} className="sq flex gap-6" style={{ "--d": "320ms" }}>
              {RANGES.map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={range === k} data-testid={`share-range-${k}`} onClick={() => setRange(k)}
                  className={`tabular pb-1.5 text-[13px] font-medium border-b transition-colors duration-200 ${range === k ? "border-navy text-navy" : "border-transparent text-muted hover:text-navy"}`}>{l}</button>
              ))}
            </div>
            {typeof chg === "number" && (
              <p className="sq tabular t-small text-muted" style={{ "--d": "400ms" }}>
                <span key={shownRange} className="fade-in inline-block" style={{ animationDuration: "500ms" }}>
                  {rangeLabel} <span data-testid="share-range-change" className={`ml-1 font-medium ${chg >= 0 ? "text-up" : "text-down"}`}>{chg > 0 ? "+" : ""}{chg.toLocaleString(LOCALE, { maximumFractionDigits: 1 })} %</span>
                </span>
              </p>
            )}
          </div>
          <div className="mt-6 min-h-[12rem] md:min-h-[16rem]">
            {hist ? <AreaChart points={hist.points} summary={hist.summary} range={shownRange} active={active} /> : <div className="h-40 md:h-56" aria-busy="true" />}
          </div>
          <div className="sq mt-6 flex flex-wrap items-center justify-between gap-4" style={{ "--d": "600ms" }}>
            <p data-testid="oslo-bors-share-timestamp" className="t-small text-muted">{label} · {t("Kursdata")}: {q.source || t("utilgjengelig")} · EQVA.OL</p>
            <Link to="/investor" data-testid="oslo-bors-share-link" className="link-arrow t-small">{t("Se aksjeinformasjon")} <ArrowRight size={16} /></Link>
          </div>
        </>
      )}
    </div>
  );
};

const parseNum = (v) => Number(String(v).replace(/\s/g, "").replace(",", "."));

export const KeyFigures = ({ figures = [], active: activeProp }) => {
  const [ref, seen] = useInView({ threshold: 0.2 });
  const active = activeProp ?? seen;
  return (
    <div ref={ref} data-testid="oslo-bors-key-figures" data-seq={active ? "in" : "out"} className="flex h-full flex-col">
      <FieldHead label={t("Siste rapporterte kvartal")} sub={figures[0]?.period_label} className="sq" style={{ "--d": "0ms" }} />
      <dl className="mt-8 flex-1 flex flex-col">
        {figures.map((f, i) => {
          const n = parseNum(f.value);
          const decimals = (String(f.value).split(/[,.]/)[1] || "").length;
          const d = 180 + i * 130;
          return (
            <div key={f.key} data-testid={`key-figure-${f.key}`} className={`flex-1 py-6 flex flex-col-reverse ${i ? "line-draw line-draw-soft" : ""}`} style={{ "--d": `${d - 140}ms` }}>
              <dt className="sq mt-2 flex items-baseline justify-between gap-4 t-small text-muted" style={{ "--d": `${d + 140}ms` }}>
                <span className="text-ink">{f.label}</span>
                {f.note && <span className="text-right">{f.note}</span>}
              </dt>
              <dd className="sq tabular flex items-baseline gap-2.5" style={{ "--d": `${d}ms` }}>
                <span className="text-[clamp(2rem,3vw,3.25rem)] font-medium leading-none tracking-[-0.03em] text-navy">
                  {Number.isFinite(n) ? <CountUp value={n} decimals={decimals} duration={1300} delay={d} active={active} /> : f.value}
                </span>
                <span className="t-small text-muted">{f.unit}</span>
              </dd>
            </div>
          );
        })}
      </dl>
      <div className="line-draw line-draw-soft mt-6 pt-6" style={{ "--d": `${180 + figures.length * 130 - 140}ms` }}>
        <Link to="/investor#rapporter" data-testid="oslo-bors-result-link" className="sq link-arrow t-small" style={{ "--d": `${180 + figures.length * 130}ms` }}>{t("Se rapporter og presentasjoner")} <ArrowRight size={16} /></Link>
      </div>
    </div>
  );
};

export const ShareFloor = ({ figures }) => (
  <section data-testid="floor-oslo-bors" data-ask-label={t("Spør om aksjen")} className="bg-surface">
    <div className="wrap floor">
      <Reveal className="grid-12 gap-y-6 items-end">
        <div className="col-span-12 lg:col-span-6">
          <FloorLabel>{t("Investor")}</FloorLabel>
          <h2 className="mt-4 t-h2">{t("EQVA på Oslo Børs")}</h2>
        </div>
        <p className="col-span-12 lg:col-span-5 lg:col-start-8 t-body text-muted max-w-[40ch]">{t("Aksjekurs, resultater og finansiell informasjon om EQVA.")}</p>
      </Reveal>
      <div className="mt-14 md:mt-20 hairline pt-10 md:pt-12 grid-12 gap-y-12">
        <div className="col-span-12 lg:col-span-7 lg:pr-12"><ShareCard /></div>
        <div className="col-span-12 lg:col-span-5 lg:border-l lg:border-line lg:pl-12"><KeyFigures figures={figures} /></div>
      </div>
    </div>
  </section>
);
